import { PageController } from '@client/components/_core/layout/PageController';
import { SegmentedControl } from '@client/components/_ui/data-entry/SegmentedControl';
import { QueryState } from '@client/components/_ui/feedback/QueryState';
import { SkeletonBlock } from '@client/components/_ui/feedback/SkeletonBlock';
import {
    INSIGHTS_DEFAULT_PERIOD,
    INSIGHTS_PERIOD_OPTIONS,
    type InsightsPeriod,
} from '@client/components/compliance-audit/insights/constants';
import { InsightsByArTable } from '@client/components/compliance-audit/insights/InsightsByArTable';
import { InsightsMetricCard } from '@client/components/compliance-audit/insights/InsightsMetricCard';
import { InsightsRecentlyFlaggedCard } from '@client/components/compliance-audit/insights/InsightsRecentlyFlaggedCard';
import { InsightsTopIssuesCard } from '@client/components/compliance-audit/insights/InsightsTopIssuesCard';
import {
    formatCountDelta,
    formatPassRate,
    formatPassRateDelta,
} from '@client/components/compliance-audit/insights/insights-format';
import { ENTITY_COLORS_CSS } from '@client/design-system/__generated__';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import { trpc } from '@client/trpc/client';
import { PERMISSIONS } from '@shared/permissions/permissions';
import { createFileRoute } from '@tanstack/react-router';
import { FileStack, MessageSquare, OctagonAlert, ShieldCheck } from 'lucide-react';
import { useState } from 'react';

// CUR-391 — Insights (reporting) tab for compliance audits: portfolio metrics
// with period-over-period deltas, top issues, recently raised incident and breach findings, and a
// per-AR breakdown table. All sections follow the selected trailing window.

export const Route = createFileRoute('/console/$orgId/tools/compliance-audit/overview/insights')({
    staticData: {
        breadcrumb: {
            title: 'Insights',
        },
        modules: ['compliance-audit'],
        permissions: [PERMISSIONS.COMPLIANCE_AUDIT_ACCESS],
    },
    component: ComplianceAuditInsightsPage,
});

function ComplianceAuditInsightsPage() {
    const { formatNumber } = useFormatter();
    const [period, setPeriod] = useState<InsightsPeriod>(INSIGHTS_DEFAULT_PERIOD);

    const insightsQuery = trpc.complianceAudit.getComplianceAuditInsightsSummary.useQuery({
        period,
    });

    return (
        <PageController route={Route}>
            <div className="flex flex-col gap-4">
                <div className="flex items-center">
                    <SegmentedControl
                        size="small"
                        variant="bordered"
                        value={period}
                        onChange={setPeriod}
                        options={INSIGHTS_PERIOD_OPTIONS}
                        aria-label="Select the reporting period"
                    />
                </div>

                <QueryState
                    query={insightsQuery}
                    loading={<SkeletonBlock variant="card" rows={8} />}
                    errorTitle="Couldn't load audit insights"
                >
                    {(data) => (
                        <>
                            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                                <InsightsMetricCard
                                    title="Files audited"
                                    icon={<FileStack size={18} />}
                                    iconColour={ENTITY_COLORS_CSS.obligations}
                                    value={formatNumber(data.filesAudited)}
                                    delta={
                                        data.filesAudited > 0
                                            ? formatCountDelta({
                                                  current: data.filesAudited,
                                                  prior: data.priorFilesAudited,
                                                  period,
                                              })
                                            : null
                                    }
                                    emptyMessage={data.filesAudited === 0 ? 'No files audited in this period' : null}
                                />
                                <InsightsMetricCard
                                    title="Audit pass rate"
                                    icon={<ShieldCheck size={18} />}
                                    iconColour={ENTITY_COLORS_CSS.controls}
                                    value={formatPassRate(data.passedAudits, data.auditsCompleted)}
                                    delta={
                                        data.auditsCompleted > 0
                                            ? formatPassRateDelta({
                                                  passed: data.passedAudits,
                                                  files: data.auditsCompleted,
                                                  priorPassed: data.priorPassedAudits,
                                                  priorFiles: data.priorAuditsCompleted,
                                                  period,
                                              })
                                            : null
                                    }
                                    emptyMessage={
                                        data.auditsCompleted === 0 ? 'No audits completed in this period' : null
                                    }
                                />
                                <InsightsMetricCard
                                    title="Incident findings raised"
                                    icon={<OctagonAlert size={18} />}
                                    iconColour={ENTITY_COLORS_CSS.incidents}
                                    value={formatNumber(data.incidentFindingsRaised)}
                                    delta={
                                        data.incidentFindingsRaised > 0
                                            ? formatCountDelta({
                                                  current: data.incidentFindingsRaised,
                                                  prior: data.priorIncidentFindingsRaised,
                                                  period,
                                                  invert: true,
                                              })
                                            : null
                                    }
                                    emptyMessage={
                                        data.incidentFindingsRaised > 0
                                            ? null
                                            : data.filesAudited === 0
                                              ? 'No files audited in this period'
                                              : 'No incident findings in this period'
                                    }
                                />
                                <InsightsMetricCard
                                    title="Complaint findings raised"
                                    icon={<MessageSquare size={18} />}
                                    iconColour={ENTITY_COLORS_CSS.complaints}
                                    value={formatNumber(data.complaintFindingsRaised)}
                                    delta={
                                        data.complaintFindingsRaised > 0
                                            ? formatCountDelta({
                                                  current: data.complaintFindingsRaised,
                                                  prior: data.priorComplaintFindingsRaised,
                                                  period,
                                                  invert: true,
                                              })
                                            : null
                                    }
                                    emptyMessage={
                                        data.complaintFindingsRaised > 0
                                            ? null
                                            : data.filesAudited === 0
                                              ? 'No files audited in this period'
                                              : 'No complaint findings in this period'
                                    }
                                />
                            </div>

                            <div className="grid items-stretch gap-4 lg:grid-cols-2">
                                <InsightsTopIssuesCard topIssues={data.topIssues} />
                                <InsightsRecentlyFlaggedCard rows={data.recentlyFlagged} />
                            </div>

                            <InsightsByArTable period={period} />
                        </>
                    )}
                </QueryState>
            </div>
        </PageController>
    );
}
